import { useTranslation } from 'next-i18next';
import { AtWorkCard } from 'preflight-core';

import { mutateString } from '../../utils/replace-language';
import { Voucher } from '../../vouchers/get-voucher';

export const AtWorkCardComponent = ({
  langCheckCount,
  techCheckCount,
  voucher,
  version,
  isGjdPage = false,
}: {
  langCheckCount: number;
  techCheckCount: number;
  voucher: Voucher | null;
  price?: string | number;
  version?: string;
  isGjdPage?: boolean;
}) => {
  const t = useTranslation('homepage').t;
  const c = useTranslation('common').t;
  const CardsData = [
    {
      cardAriaLabel: 'Language quality check',
      heading: `${langCheckCount} ${c('language_checks')}`,
      content: t('section3.language_checks_description'),
      mainImage: '/assets/landingPage/language-check.svg',
      floatingImage: '/assets/language-check-icon.svg',
      mainImageAlt: 'Language check main image',
      floatingImageAlt: 'language check icon',
      cardType: 'languageCheck',
      floatingTitle: c('language_checks'),
      floatingInfo: `${t('section3.parameters')} (${langCheckCount})`,
      contentAriaLabel: 'Language quality check',
    },
    {
      cardAriaLabel: 'Technical requirements',
      heading: `${techCheckCount} ${c('technical_checks')}`,
      content: t('section3.technical_checks_description'),
      mainImage: '/assets/landingPage/technical-check.svg',
      floatingImage: '/assets/landingPage/technical-check-icon.svg',
      mainImageAlt: 'Technical check main image',
      floatingImageAlt: 'Technical check icon',
      cardType: 'technicalCheck',
      // smallImage: '/assets/technical-check-small.png',
      contentAriaLabel: 'Technical requirements',
    },
    {
      cardAriaLabel: 'Detailed free report',
      heading: t('section3.ai_powered_report'),
      content: mutateString(t('section3.ai_powered_report_description'), {
        '{langTechChecks}': `${langCheckCount + techCheckCount}`,
      }),
      mainImage: '/assets/landingPage/ai-powered.svg',
      floatingImage: '/assets/ai-powered-icon.svg',
      mainImageAlt: 'AI Powered Report',
      floatingImageAlt: 'AI Icon',
      cardType: 'aiPowered',
      contentAriaLabel: 'Detailed free report',
    },
    {
      cardAriaLabel: 'Download an edited version of your manuscript',
      heading: t('section3.downloadable_edited_file'),
      content: mutateString(
        t('section3.downloadable_edited_file_description'),
        voucher ? { '{type}': c('voucher') } : { '{type}': c('fee') },
      ),
      mainImage: '/assets/landingPage/edited-file.svg',
      floatingImage: '/assets/edited-file-icon.svg',
      mainImageAlt: 'Downloadable Edited File',
      floatingImageAlt: 'Download Icon',
      cardType: 'editedFiles',
      contentAriaLabel: 'Download an edited version of your manuscript',
    },
  ];

  const CardsDataV1 = [
    {
      cardAriaLabel: 'Language quality check',
      heading: `${langCheckCount} ${c('language_checks')}`,
      content: t('section4_v1.language_checks_description'),
      mainImage: '/v1-landing-page/language-check.svg',
      floatingImage: '',
      mainImageAlt: 'Language check main image',
      floatingImageAlt: 'language check icon',
      cardType: 'languageCheck',
      floatingTitle: c('language_checks'),
      floatingInfo: `${t('section4_v1.parameters')} (${langCheckCount})`,
      contentAriaLabel: 'Language quality check',
    },
    {
      cardAriaLabel: 'Technical requirements',
      heading: `${techCheckCount} ${c('technical_checks')}`,
      content: t('section4_v1.technical_checks_description'),
      mainImage: '/v1-landing-page/technical-check.svg',
      floatingImage: '',
      mainImageAlt: 'Technical check main image',
      floatingImageAlt: 'Technical check icon',
      cardType: 'technicalCheck',
      // smallImage: '/assets/technical-check-small.png',
      contentAriaLabel: 'Technical requirements',
    },
    {
      cardAriaLabel: 'Detailed free report',
      heading: t('section4_v1.ai_powered_report'),
      content: mutateString(t('section4_v1.ai_powered_report_description'), {
        '{langTechChecks}': `${langCheckCount + techCheckCount}`,
      }),
      mainImage: '/v1-landing-page/ai-powered-report.svg',
      floatingImage: '',
      mainImageAlt: 'AI Powered Report',
      floatingImageAlt: 'AI Icon',
      cardType: 'aiPowered',
      contentAriaLabel: 'Detailed free report',
    },
    {
      cardAriaLabel: 'Download an edited version of your manuscript',
      heading: isGjdPage
        ? t('gjd.downloadable_edited_file')
        : t('section4_v1.downloadable_edited_file'),
      content: isGjdPage
        ? t('gjd.downloadable_edited_file_description')
        : voucher
        ? t('section4_v1.downloadable_edited_file_description_voucher')
        : t('section4_v1.downloadable_edited_file_description_free'),
      mainImage: '/v1-landing-page/download-edited-file.svg',
      floatingImage: '',
      mainImageAlt: 'Downloadable Edited File',
      floatingImageAlt: 'Download Icon',
      cardType: 'editedFiles',
      contentAriaLabel: 'Download an edited version of your manuscript',
    },
  ];

  return (
    <>
      {version && version === 'v1' ? (
        <div
          aria-label="At work cards"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '30px',
            marginTop: '-20px',
          }}
        >
          {CardsDataV1.map((data, index) =>
            langCheckCount === 0 && data.cardType === 'languageCheck' ? (
              <></>
            ) : techCheckCount === 0 && data.cardType === 'technicalCheck' ? (
              <></>
            ) : (
              <AtWorkCard key={index} {...data} version={version} />
            ),
          )}
        </div>
      ) : (
        <div
          aria-label="At work cards"
          style={{ display: 'flex', flexWrap: 'wrap', gap: '30px' }}
        >
          {CardsData.map((data, index) =>
            langCheckCount === 0 && data.cardType === 'languageCheck' ? (
              <></>
            ) : techCheckCount === 0 && data.cardType === 'technicalCheck' ? (
              <></>
            ) : (
              <AtWorkCard key={index} {...data} version={version} />
            ),
          )}
        </div>
      )}
    </>
  );
};
